import { createStore, useStore } from 'zustand';

import { alarm } from '@lib/alarm';
import { authStore } from '@stores/auth';
import { logger } from '@lib/logger';
import { lookupsStore } from '@stores/lookups';
import { pendingToRoutedOrders } from '@lib/api/updateOrderStatus';
import { FirestoreOrderSummary, OrderSummary } from '@lib/types';
import { Unsubscribe, firebase } from '@lib/firebase';
import { startOrderForegroundService, stopOrderForegroundService } from '@services/orderForegroundService';

import { fromFirestoreOrderSummaryToOrderSummary } from './fromFirestoreOrderSummaryToOrderSummary';

/* -------------------------------------------------------------------------- */
/*                               STORE TYPES                                  */
/* -------------------------------------------------------------------------- */

interface NewOrdersStore {
  loading: boolean;
  error: Error | null;
  orders: OrderSummary[];
  retryCount: number;
  lastSubscribedQuery: string | null; // Track the query string
  
  subscribe: () => void;
  unsubscribe: () => void;

  onSnapshotUnsubscribe: Unsubscribe | null;
}

const MAX_RETRY_DELAY = 30000; // Max 30 seconds
const INITIAL_RETRY_DELAY = 1000; // Start with 1 second

/* -------------------------------------------------------------------------- */
/*                              NEW ORDERS STORE                               */
/* -------------------------------------------------------------------------- */

export const newOrdersStore = createStore<NewOrdersStore>((set, get) => ({
  error: null,
  loading: false,
  orders: [],
  retryCount: 0,
  onSnapshotUnsubscribe: null,
  lastSubscribedQuery: null,

  subscribe: () => {
    const lookups = lookupsStore.getState().value;
    if (!lookups) return;

    // 1. Create a unique string key for the current query
    const queryKey = JSON.stringify(lookups.query);

    // 2. AVOID REDUNDANT SUBSCRIPTIONS:
    // If we are already listening to this exact query, do nothing.
    if (get().onSnapshotUnsubscribe && get().lastSubscribedQuery === queryKey) {
      return;
    }
    // prevent duplicate subscriptions
    get().onSnapshotUnsubscribe?.();

    set({ loading: true, lastSubscribedQuery: queryKey });

    const onSnapshotUnsubscribe =
      firebase.onMultipleQueriesSnapshot<FirestoreOrderSummary>({
        collection: 'ordersSummary',
        queries: [
          {
            where: [['state', '==', 'PENDING'], lookups.query],
            order: [['createdAt', 'asc']],
          },
          {
            where: [['state', '==', 'ROUTED'], lookups.query],
            order: [['createdAt', 'asc']],
          },
          {
            where: [['state', '==', 'SEEN'], lookups.query],
            order: [['createdAt', 'asc']],
          },
        ],
        onNext: (firestoreOrders) => {
          try {
            const orders = firestoreOrders
            .filter(doc => !!doc)
            .map(
              fromFirestoreOrderSummaryToOrderSummary
            );
            // We get old orders BEFORE updating the state
            const oldOrders = get().orders;
            
            orders.length ? alarm.play() : alarm.stop();

            set({
              loading: false,
              error: null,
              orders,
            });

            // Fire and forget the async task, but handle the rejection explicitly
            pendingToRoutedOrders(orders, oldOrders).catch((err) => {
              logger.error('Background routing update failed', err);
            });
            
          } catch (error) {
            // This catches issues in mapping or the 'set' logic
            logger.error('Mapping error in new orders', error);
          }
        },
        onError: (error) => {
          logger.error('error in new orders ', error);
          
          const currentRetry = get().retryCount;
          
          // Calculate delay: 1s, 2s, 4s, 8s... up to 30s
          const delay = Math.min(
            INITIAL_RETRY_DELAY * Math.pow(2, currentRetry), 
            MAX_RETRY_DELAY
          );

          set({ 
            error, 
            loading: false, 
            retryCount: currentRetry + 1 
          });

          // Schedule the retry
          setTimeout(() => {
            // Only retry if we haven't unsubscribed in the meantime
            if (get().onSnapshotUnsubscribe) {
              get().subscribe();
            }
          }, delay);
        },
        sortPredicate: (item) => {
          try {
            // Latency compensation: item might not have createdAt yet on first local write
            return item.createdAt?.toDate?.()?.getTime() ?? Date.now();
          } catch (e) {
            return Date.now();
          }
        }
      });

    set({ onSnapshotUnsubscribe });
  },

  unsubscribe: () => {
    alarm.stop();
    get().onSnapshotUnsubscribe?.();
    set({
      onSnapshotUnsubscribe: null,
      lastSubscribedQuery: null,
      orders: [],
    });
  },
}));

/* -------------------------------------------------------------------------- */
/*                       LOOKUPS → ORDERS SYNC (CRITICAL)                      */
/* -------------------------------------------------------------------------- */

function onLookupsReady() {
  if (!lookupsStore.getState().value) return;

  startOrderForegroundService();
  newOrdersStore.getState().subscribe();
}

// future changes
lookupsStore.subscribe((state, prevState) => {
  const currentQuery = state.value?.query;
  const previousQuery = prevState?.value?.query;

  // Only trigger onLookupsReady if the actual query object has changed
  if (currentQuery && JSON.stringify(currentQuery) !== JSON.stringify(previousQuery)) {
    onLookupsReady();
  }
});

// initial state (FIXES ANDROID)
if (lookupsStore.getState().value) {
  onLookupsReady();
}

/* -------------------------------------------------------------------------- */
/*                             LOGOUT CLEANUP                                  */
/* -------------------------------------------------------------------------- */

authStore.subscribe((state, prevState) => {
  if (prevState?.computed?.isLoggedIn && !state.computed.isLoggedIn) {
    stopOrderForegroundService();
    newOrdersStore.getState().unsubscribe();
  }
});

/* -------------------------------------------------------------------------- */
/*                                   HOOKS                                    */
/* -------------------------------------------------------------------------- */

export const useNewOrdersStore = () => useStore(newOrdersStore);

export const useNewOrder = (id: string) => {
  const { orders } = useNewOrdersStore();
  return { order: orders.find((o) => o.uuid === id) } as const;
};